import { useMemo, useState, type ReactElement } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { SettingsLayout, type SettingsNavItem } from "@playbookai/ui";
import { useAuthStore } from "../../auth/auth-store.js";
import {
  SettingsLeaveGuardProvider,
  useGuardedNavigate,
} from "../../settings/leave-guard.js";
import {
  filterSettingsSections,
  settingsSectionById,
  settingsSectionIdForPath,
  visibleSettingsSections,
} from "../../settings/sections.js";

/**
 * The Settings host (PB-5134, Direction B): one shared grouped rail for every
 * Settings page, with section search in the rail, Account as the index, and
 * KPI Manager's sub-views as child items. The rail reads the same registry the
 * universal search does, so the two vocabularies cannot drift.
 */
function SettingsPageHost(): ReactElement {
  const navigate = useGuardedNavigate();
  const location = useLocation();
  const claims = useAuthStore((state) => state.claims);
  const [query, setQuery] = useState("");

  const activeId = settingsSectionIdForPath(location.pathname);

  const sections = useMemo(() => visibleSettingsSections(claims), [claims]);
  const visible = useMemo(
    () => filterSettingsSections(sections, query),
    [sections, query],
  );
  const items = useMemo<SettingsNavItem[]>(
    () =>
      visible.map((section) => ({
        id: section.id,
        label: section.label,
        ...(section.parentId ? {} : { description: section.description }),
        ...(section.icon ? { icon: section.icon } : {}),
        group: section.group,
        ...(section.parentId ? { parentId: section.parentId } : {}),
      })),
    [visible],
  );

  const search = (
    <label className="flex items-center gap-2 rounded-md border border-pb-control-border bg-pb-card px-2 py-1.5 text-pb-text-muted focus-frame">
      <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {/* eslint-disable-next-line ds/no-hand-built -- kept: Input sm is a 28px text-xs field and md a 36px one; no PR 3 look row approves that. */}
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search settings"
        placeholder="Search settings"
        className="min-w-0 flex-1 bg-transparent text-sm text-pb-text outline-none placeholder:text-pb-text-muted"
      />
    </label>
  );

  return (
    <SettingsLayout
      items={items}
      activeId={activeId}
      onNavigate={(id) => {
        const route = settingsSectionById(id)?.route;
        if (route) navigate(route);
      }}
      title="Settings"
      subtitle="Preferences"
      navHeaderSlot={
        <div>
          {search}
          {/* Kept as today's 12px line: EmptyState size="sm" sets 11px
              type, a look change no PR 3 row approves. */}
          {visible.length === 0 ? (
            <p role="status" className="mt-2 text-xs text-pb-text-muted">
              No settings match "{query.trim()}".
            </p>
          ) : null}
        </div>
      }
      // Every Settings page shares the console lane the Admin console uses
      // (90rem, PB-5216 — superseding d35's relaxed lane): forms keep a
      // reading width through SettingsPageLayout, tables take the width. KPI
      // Manager is scrolled by the lane, with the canvas' page header and
      // sub-tabs drawn by the feature; only its child pages (Create, a KPI)
      // get a breadcrumb lane back to the collections.
      contentWidth="console"
      className="min-h-full"
    >
      <Outlet />
    </SettingsLayout>
  );
}

/** The Settings host: the leave guard wraps the rail so every host-driven
 * navigation asks before a dirty draft is dropped (t09). */
export function SettingsPage(): ReactElement {
  return (
    <SettingsLeaveGuardProvider>
      <SettingsPageHost />
    </SettingsLeaveGuardProvider>
  );
}
